import React, { useCallback, useMemo } from 'react';
import { TABLE_SELECT_COLUMN_ID } from '@antimatterhq/ui/constants/table';
import { TableColumn } from '@antimatterhq/ui/types/TableColumn';
import Table from '@antimatterhq/ui/components/table';
import {
  MemoizedCustomCell,
  TableRowsSelection,
} from '@antimatterhq/ui/hooks/table/useTableRows';
interface CustomHeaderCellProps {
  column: TableColumn;
}

interface CustomCellProps<T> {
  item: T;
  column: TableColumn;
}

export type VirtualizedCustomBodyCells<T> = Record<
  string,
  React.ComponentType<CustomCellProps<T>> | React.ReactNode
>;

export type VirtualizedCustomHeaderCells = Record<
  string,
  React.ComponentType<CustomHeaderCellProps> | React.ReactNode
>;

type UseVirtuosoTableRowsProps<T> = {
  columns: TableColumn[];
  isLoading?: boolean;
  customHeaderCells?: VirtualizedCustomHeaderCells;
  customBodyCells?: VirtualizedCustomBodyCells<T>;
  selection?: TableRowsSelection;
  'data-testid'?: string;
};

interface VirtuosoTableRenderersResult<T> {
  fixedHeaderContent: () => React.ReactNode;
  itemContent: (index: number, item: T) => React.ReactNode;
  selectedRows?: string[];
  onRowClick?: (row: T) => void;
}

export function useVirtuosoTableRows<T extends { id: string }>(
  props: UseVirtuosoTableRowsProps<T>,
): VirtuosoTableRenderersResult<T> {
  const {
    columns,
    selection,
    'data-testid': dataTestId = 'virtuoso-table',
  } = props;

  const customHeaderCells = useMemo(
    () => props.customHeaderCells,
    [props.customHeaderCells],
  );

  const customBodyCells = useMemo(
    () => props.customBodyCells,
    [props.customBodyCells],
  );

  const {
    isSelected,
    selectedRows,
    onToggleSelectAll,
    isAllSelected,
    onToggleSelect,
  } = useMemo(() => selection ?? ({} as TableRowsSelection), [selection]);

  const renderHeaderCell = useCallback(
    (column: TableColumn) => {
      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        (!onToggleSelectAll || isAllSelected === undefined)
      ) {
        console.warn(
          'TABLE_SELECT_COLUMN_ID is defined but Table.SelectHeaderCell is missing required props: onToggleSelectAll and isAllSelected',
        );
      }

      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        onToggleSelectAll &&
        isAllSelected !== undefined
      ) {
        return (
          <Table.Head key={column.id} column={column}>
            <Table.SelectHeaderCell
              isAllSelected={isAllSelected}
              onToggleSelectAll={onToggleSelectAll}
            />
          </Table.Head>
        );
      } else if (typeof customHeaderCells?.[column.id] === 'function') {
        const CustomCell = customHeaderCells?.[
          column.id
        ] as React.ComponentType<CustomHeaderCellProps>;

        return (
          <Table.Head key={column.id} column={column}>
            <CustomCell column={column} />
          </Table.Head>
        );
      } else if (customHeaderCells?.[column.id] !== undefined) {
        return (
          <Table.Head key={column.id} column={column}>
            {customHeaderCells?.[column.id] as React.ReactNode}
          </Table.Head>
        );
      } else {
        return (
          <Table.Head key={column.id} column={column}>
            {column.title}
          </Table.Head>
        );
      }
    },
    [customHeaderCells, isAllSelected, onToggleSelectAll],
  );

  const fixedHeaderContent = useCallback(() => {
    return (
      <Table.Row data-testid={`${dataTestId}-header-row`}>
        {columns.map((column) => renderHeaderCell(column))}
      </Table.Row>
    );
  }, [columns, renderHeaderCell, dataTestId]);

  const renderBodyCell = useCallback(
    (item: T, column: TableColumn) => {
      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        (!isSelected || !onToggleSelect)
      ) {
        console.warn(
          'TABLE_SELECT_COLUMN_ID is defined but Table.SelectCell is missing required props: isSelected and onToggleSelect',
        );
      }

      if (
        column.id === TABLE_SELECT_COLUMN_ID &&
        isSelected &&
        onToggleSelect
      ) {
        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${dataTestId}-cell-${column.id}-${item.id}`}
          >
            <Table.SelectCell
              selected={isSelected(item.id)}
              id={item.id}
              onToggleSelect={onToggleSelect}
              data-testid={`${dataTestId}-select-cell-${item.id}`}
            />
          </Table.Cell>
        );
      } else if (typeof customBodyCells?.[column.id] === 'function') {
        const CustomCell = customBodyCells?.[column.id] as React.ComponentType<
          CustomCellProps<T>
        >;

        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${dataTestId}-cell-${column.id}-${item.id}`}
          >
            <MemoizedCustomCell
              component={CustomCell}
              item={item}
              column={column}
            />
          </Table.Cell>
        );
      } else if (customBodyCells?.[column.id] !== undefined) {
        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${dataTestId}-cell-${column.id}-${item.id}`}
          >
            {customBodyCells?.[column.id] as React.ReactNode}
          </Table.Cell>
        );
      } else {
        return (
          <Table.Cell
            column={column}
            key={column.id}
            data-testid={`${dataTestId}-cell-${column.id}-${item.id}`}
          >
            <Table.SafeValueWrapper
              value={item[column.id as keyof T]}
              data-testid={`${dataTestId}-value-${column.id}-${item.id}`}
            />
          </Table.Cell>
        );
      }
    },
    [isSelected, onToggleSelect, customBodyCells, dataTestId],
  );

  // Define row renderer with a display name
  const itemContent = useCallback(
    (_index: number, item: T) => {
      return columns.map((column) => renderBodyCell(item, column));
    },
    [columns, renderBodyCell],
  );

  return {
    fixedHeaderContent,
    itemContent,
    selectedRows,
  };
}
